import { computed, reactive } from 'vue'
import type { AdminAPIResource, AdminMenu, Envelope } from '../types/domain'

const defaultApiBase = import.meta.env.MODE === 'test'
  ? 'https://api-test.xinjxing.com/api/v1'
  : import.meta.env.MODE === 'production'
    ? 'https://api.xinjxing.com/api/v1'
    : '/api/v1'
const apiBase = (import.meta.env.VITE_API_BASE_URL || defaultApiBase).replace(/\/$/, '')
let accessToken = ''
let refreshToken = ''
let refreshPromise: Promise<boolean> | null = null
let sessionRestoreBlocked = false

export interface AdminSessionSummary { id: number; account: string; name: string }
interface AdminAuthResult { accessToken: string; refreshToken?: string; expiresIn: number; refreshExpiresIn: number; admin: AdminSessionSummary }

export const adminSession = reactive<{ authenticated: boolean; ready: boolean; admin: AdminSessionSummary | null; menus: AdminMenu[]; apiResources: AdminAPIResource[]; voucherEnabled: boolean }>({
  authenticated: false,
  ready: false,
  admin: null,
  menus: [],
  apiResources: [],
  voucherEnabled: false,
})
const adminPermissionCodes = computed(() => new Set(adminSession.apiResources.map((resource) => resource.code)))

export class AdminAPIError extends Error {
  constructor(public code: string, message: string, public requestId = '', public details?: { field?: string; reason?: string }) { super(message) }
}

export function setAdminSession(access: string, refresh = '', admin?: AdminSessionSummary) {
  accessToken = access
  refreshToken = refresh
  sessionRestoreBlocked = false
  adminSession.authenticated = access !== ''
  adminSession.ready = true
  if (admin) adminSession.admin = admin
  if (access) void loadAdminMenus()
}

export function clearAdminSession() {
  accessToken = ''
  refreshToken = ''
  adminSession.authenticated = false
  adminSession.ready = true
  adminSession.admin = null
  adminSession.menus = []
  adminSession.apiResources = []
  adminSession.voucherEnabled = false
}

async function loadAdminMenus() {
  try {
    const [me, capabilities] = await Promise.all([
      adminRequest<{ menus?: AdminMenu[]; apiResources?: AdminAPIResource[] }>('/admin/me'),
      adminRequest<{ voucher?: boolean }>('/capabilities', { auth: false }).catch(() => null),
    ])
    adminSession.menus = Array.isArray(me?.menus) ? me.menus : []
    adminSession.apiResources = Array.isArray(me?.apiResources) ? me.apiResources : []
    adminSession.voucherEnabled = capabilities?.voucher === true
  } catch { adminSession.menus = []; adminSession.apiResources = []; adminSession.voucherEnabled = false }
}

export function hasAdminPermission(required: string | readonly string[]) {
  return (Array.isArray(required) ? required : [required]).every((code) => adminPermissionCodes.value.has(code))
}

export async function loginAdmin(account: string, password: string) {
  const result = await adminRequest<AdminAuthResult>('/admin/auth/login', { method: 'POST', auth: false, retry: false, data: { account, password } })
  setAdminSession(result.accessToken, result.refreshToken || '', result.admin)
  return result.admin
}

export async function restoreAdminSession() {
  if (accessToken) return true
  if (sessionRestoreBlocked) return false
  return refreshAdminSession()
}

export async function logoutAdmin() {
  try {
    if (!accessToken && !sessionRestoreBlocked) await refreshAdminSession()
    if (accessToken) await adminRequest('/admin/auth/logout', { method: 'POST' })
  } catch {
    // Local session must still be cleared when the server is unreachable.
  } finally {
    sessionRestoreBlocked = true
    clearAdminSession()
  }
}

export function resolveAdminResourceURL(path: string) {
  if (/^https?:\/\//i.test(path)) return path
  if (/^https?:\/\//i.test(apiBase)) {
    const origin = apiBase.match(/^(https?:\/\/[^/]+)/i)?.[1] || ''
    return path.startsWith('/') ? `${origin}${path}` : `${apiBase}/${path}`
  }
  return path.startsWith('/') ? path : `${apiBase}/${path}`
}

interface RequestOptions { method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'; data?: unknown; auth?: boolean; retry?: boolean; headers?: Record<string, string> }

export async function adminRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (options.auth !== false && !accessToken && !await restoreAdminSession()) {
    throw new AdminAPIError('UNAUTHENTICATED', '后台登录状态无效')
  }
  let response: Response
  try {
    response = await fetch(`${apiBase}${path}`, {
      method: options.method || 'GET',
      headers: { 'Content-Type': 'application/json', ...(options.auth === false || !accessToken ? {} : { Authorization: `Bearer ${accessToken}` }), ...options.headers },
      body: options.data === undefined ? undefined : JSON.stringify(options.data),
      credentials: 'include',
    })
  } catch { throw new AdminAPIError('NETWORK_ERROR', '网络连接失败') }
  if (response.status === 401 && options.auth !== false && options.retry !== false && await refreshAdminSession()) return adminRequest<T>(path, { ...options, retry: false })
  const envelope = await response.json().catch(() => ({ code: 'NETWORK_ERROR', message: '响应格式无效', data: null, requestId: '' })) as Envelope<T>
  if (!response.ok || envelope.code !== 'OK') throw new AdminAPIError(envelope.code || 'NETWORK_ERROR', envelope.message || '请求未完成', envelope.requestId, envelope.details)
  return envelope.data
}

async function refreshAdminSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const result = await adminRequest<AdminAuthResult>('/admin/auth/refresh', { method: 'POST', auth: false, retry: false, data: refreshToken ? { refreshToken } : undefined })
      setAdminSession(result.accessToken, result.refreshToken || '', result.admin)
      return true
    } catch { clearAdminSession(); return false } finally { refreshPromise = null }
  })()
  return refreshPromise
}

export async function downloadAdminCSV(path: string, filename: string) {
  const response = await adminRawRequest(path)
  if (!response.ok) throw new AdminAPIError(response.status === 401 ? 'UNAUTHENTICATED' : 'NETWORK_ERROR', '导出失败')
  await triggerDownload(response, filename)
}

async function adminRawRequest(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
	if (!accessToken && !await restoreAdminSession()) throw new AdminAPIError('UNAUTHENTICATED', '后台登录状态无效')
	let response: Response
	try {
		response = await fetch(`${apiBase}${path}`, { ...init, headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), ...(init.headers || {}) }, credentials: 'include' })
	} catch { throw new AdminAPIError('NETWORK_ERROR', '网络连接失败') }
	if (response.status === 401 && retry && await refreshAdminSession()) return adminRawRequest(path, init, false)
	return response
}

async function responseError(response: Response, fallback: string): Promise<never> {
	const envelope = await response.json().catch(() => ({ code: 'NETWORK_ERROR', message: fallback, requestId: '' })) as Envelope<unknown>
	throw new AdminAPIError(envelope.code || 'NETWORK_ERROR', envelope.message || fallback, envelope.requestId)
}

export async function fetchAdminImage(path: string): Promise<Blob> {
	const response = await adminRawRequest(path)
	if (!response.ok) await responseError(response, '图片加载失败')
	const blob = await response.blob()
	if (!blob.type.toLowerCase().startsWith('image/')) throw new AdminAPIError('NETWORK_ERROR', '图片格式无效')
	return blob
}

async function triggerDownload(response: Response, filename: string) {
  const url = URL.createObjectURL(await response.blob())
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = filename; anchor.click(); URL.revokeObjectURL(url)
}

export async function exportAdminCSV(path: string, filename: string, transformText?: (content: string) => string) {
	const response = await adminRawRequest(path, { method: 'POST' })
	if (!response.ok) throw new AdminAPIError(response.status === 401 ? 'UNAUTHENTICATED' : 'NETWORK_ERROR', '导出失败')
	if (!transformText) {
		await triggerDownload(response, filename)
		return
	}
	const content = transformText(await response.text())
	const transformed = new Response(new Blob([content], { type: 'text/csv;charset=utf-8' }))
	await triggerDownload(transformed, filename)
}

export async function importAdminLogistics(file: File) {
	const body = new FormData()
	body.append('file', file)
	const response = await adminRawRequest('/admin/orders/import-jobs', { method: 'POST', body })
	if (!response.ok) await responseError(response, '导入失败')
	const envelope = await response.json() as Envelope<{ imported: number; skipped: number; total: number }>
	if (envelope.code !== 'OK') throw new AdminAPIError(envelope.code, envelope.message, envelope.requestId)
	return envelope.data
}
